import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FlamingoResponse, FlamingoTypes, FeatureFlag } from '@mode/shared/contract-common';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { getInitialFlagValue } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { HelixResponseStatus } from 'libs/shared/contract-common/src/lib/flamingo/flamingo-response.types';
import { Observable, TimeoutError, from, lastValueFrom, of, switchMap, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMapTo, tap, timeout } from 'rxjs/operators';
import { convertMicrosecondsToMilliseconds } from './http-interceptors/flamingo-select.interceptor';

@Injectable({
  providedIn: 'root',
})
export class FlamingoClientService {
  private readyCache: Map<string, Promise<FlamingoResponse.ReadyResponse>>;
  private readyHelixCache: Map<string, Promise<FlamingoResponse.ReadyResponse>>;
  private routeTrafficToEcs: boolean;

  constructor(
    private http: HttpClient,
    private bugsnag: BugsnagService,
    private observabilityService: ObservabilityService
  ) {
    this.readyCache = new Map();
    this.readyHelixCache = new Map();
    this.routeTrafficToEcs = getInitialFlagValue(FeatureFlag.RouteTrafficToEcs) as unknown as boolean;
  }

  private getHeaders(signature: string, datasetName: string, vizToken?: string, dbtMetricToken?: string) {
    const defaultHeaders: { [key: string]: string } = {
      'X-Dataset-Name': datasetName,
      'X-Mode-DBT': `${!!dbtMetricToken}`,
      'Content-Type': 'application/json',
      Authorization: `MOD1-HMAC-SHA256 Signature=${signature}`,
    };

    if (vizToken) {
      defaultHeaders['X-Mode-Meta'] = `Visualization=${vizToken}`;
    }

    if (this.routeTrafficToEcs) {
      return {
        ...defaultHeaders,
        'Mode-Flamingo-UseECS': 'enabled',
      };
    }
    return defaultHeaders;
  }

  private getReadyUrl(host: string, datasetName: string) {
    return `//${host}/datasets/${datasetName}`;
  }

  private getHelixReadyUrl(host: string, datasetName: string) {
    return `//${host}/helix/datasets/v1/${datasetName}`;
  }

  catchReadyError<T extends FlamingoResponse.HelixReadyResponse | FlamingoResponse.ReadyResponse>(datasetName: string) {
    return catchError<T, Observable<FlamingoResponse.ReadyResponse>>((err) => {
      this.readyHelixCache.delete(datasetName); // Only cache successful ready response
      this.readyCache.delete(datasetName);

      if (err instanceof HttpErrorResponse) {
        let response: FlamingoResponse.ReadyResponse | undefined;
        switch (err.status) {
          case 404: {
            response = {
              status: err.status,
              message: err.statusText,
              type: FlamingoResponse.ResponseType.DatasetNotFound,
            };
            break;
          }
          case 412: {
            response = {
              status: err.status,
              message: err.statusText,
              type: FlamingoResponse.ResponseType.SignatureExpired,
            };
            break;
          }
          default: {
            response = {
              status: err.status,
              message: err.statusText,
              type: FlamingoResponse.ResponseType.ServerError,
            };
            break;
          }
        }

        return of(response);
      } else if (err instanceof TimeoutError) {
        return of({ type: FlamingoResponse.ResponseType.TimeoutError } as FlamingoResponse.ReadyResponse);
      }

      // Other errors will rethrow, and likely will cause undefined behavior
      return throwError(() => err);
    });
  }

  catchSelectError(host: string, signature: string, datasetName: string) {
    let has404Retried = false;

    return catchError(
      (
        err: unknown,
        caught: Observable<FlamingoResponse.SelectResponse | FlamingoResponse.HelixSelectFinalResponse>
      ) => {
        if (err instanceof HttpErrorResponse) {
          let response: FlamingoResponse.SelectResponse | FlamingoResponse.HelixSelectFinalResponse | undefined;
          const responseStatus = err.status;

          switch (responseStatus) {
            case 400: {
              // Angular will put the response body in the error property
              const flamingoResponse = err.error as FlamingoResponse.FlamingoErrorPayload;
              response = {
                status: responseStatus,
                message: err.statusText,
                type: FlamingoResponse.ResponseType.ServiceError,
                payload: flamingoResponse,
              };
              break;
            }
            case 404: {
              this.readyHelixCache.delete(datasetName);
              this.readyCache.delete(datasetName);

              if (!has404Retried) {
                has404Retried = true;
                return from(this.ready(host, signature, datasetName)).pipe(switchMapTo(caught));
              }

              response = {
                status: responseStatus,
                message: err.statusText,
                type: FlamingoResponse.ResponseType.DatasetNotFound,
              };
              break;
            }
            case 412: {
              response = {
                status: responseStatus,
                message: err.statusText,
                type: FlamingoResponse.ResponseType.SignatureExpired,
              };
              break;
            }
            case 500: {
              response = {
                status: responseStatus,
                message: err.statusText,
                type: FlamingoResponse.ResponseType.ServerError,
              };
              break;
            }
            default: {
              response = {
                status: responseStatus,
                message: err.statusText,
                type: FlamingoResponse.ResponseType.UnknownError,
              };
              break;
            }
          }
          return of(response);
        } else if (err instanceof TimeoutError) {
          this.bugsnag.notify(err, (event: any) => {
            event.groupingHash = 'FlamingoSelectTimeout';

            event.addMetadata('debugInfo', {
              datasetName,
            });
          });
          return of({ type: FlamingoResponse.ResponseType.TimeoutError } as FlamingoResponse.SelectErrorResponse);
        }
        return throwError(() => err);
      }
    );
  }

  handleHelixReadyResponse(results: FlamingoResponse.HelixDatasetResults, datasetName: string) {
    let response: FlamingoResponse.HelixReadyResponse;

    const datasetResult = results[datasetName];
    switch (datasetResult.state) {
      case FlamingoResponse.DatasetState.Valid:
        response = {
          result: datasetResult,
          type: FlamingoResponse.ResponseType.ReadyCommitted,
        };
        break;
      case FlamingoResponse.DatasetState.Invalid:
        response = {
          result: datasetResult,
          type: FlamingoResponse.ResponseType.DatasetFailed,
        };
        break;
      case FlamingoResponse.DatasetState.ValidationInProgress:
        response = {
          result: datasetResult,
          type: FlamingoResponse.ResponseType.HelixInProgress,
        };
        break;
      default:
        response = {
          status: 520,
          message: 'Server unreachable',
          type: FlamingoResponse.ResponseType.ServerError,
        };
        break;
    }

    return response;
  }

  handleReadyResponse(result: FlamingoResponse.DatasetResult): FlamingoResponse.ReadyResponse {
    switch (result.state) {
      case FlamingoResponse.DatasetState.Committed:
        return {
          result,
          type: FlamingoResponse.ResponseType.ReadyCommitted,
        };
      case FlamingoResponse.DatasetState.Failed:
        return {
          result,
          type: FlamingoResponse.ResponseType.DatasetFailed,
        };
      default:
        return {
          status: 520,
          message: 'Server unreachable',
          type: FlamingoResponse.ResponseType.ServerError,
        };
    }
  }

  // TODO: Delete the extra overrides once all services have been migrated to Helix
  ready(
    host: string,
    signature: string,
    datasetName: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.ReadyResponse>;
  ready(
    host: string,
    signature: string,
    datasetName: string,
    useHelix: false,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.ReadyResponse>;
  ready(
    host: string,
    signature: string,
    datasetName: string,
    useHelix: true,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.HelixReadyResponse>;
  ready(
    host: string,
    signature: string,
    datasetName: string,
    useHelix?: boolean | string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.ReadyResponse | FlamingoResponse.HelixReadyResponse> {
    let readyPromise;

    // If useHelix overload is not used, then need to shift all remaining optional parameters
    if (typeof useHelix === 'string') {
      dbtMetricToken = useHelix;
    }

    if (useHelix !== false) {
      const cached = this.readyHelixCache.get(datasetName);
      if (cached != null) {
        return cached;
      }

      const url = this.getHelixReadyUrl(host, datasetName);
      readyPromise = lastValueFrom(
        this.http
          .get<FlamingoResponse.HelixDatasetResults>(url, {
            headers: this.getHeaders(signature, datasetName, undefined, dbtMetricToken),
          })
          .pipe(
            map((results) => {
              return this.handleHelixReadyResponse(results, datasetName);
            }),
            tap((response) => {
              if (response.type !== FlamingoResponse.ResponseType.ReadyCommitted) {
                this.readyHelixCache.delete(datasetName); // Only cache successful ready response
              }
            }),
            this.catchReadyError(datasetName)
          )
      );
    } else {
      const cached = this.readyCache.get(datasetName);
      if (cached != null) {
        return cached;
      }
      const url = this.getReadyUrl(host, datasetName);
      readyPromise = lastValueFrom(
        this.http
          .get<FlamingoResponse.DatasetResult>(url, {
            headers: this.getHeaders(signature, datasetName, undefined, dbtMetricToken),
          })
          .pipe(
            map<FlamingoResponse.DatasetResult, FlamingoResponse.ReadyResponse>((result) => {
              return this.handleReadyResponse(result);
            }),
            tap((response: FlamingoResponse.ReadyResponse) => {
              if (response.type !== FlamingoResponse.ResponseType.ReadyCommitted) {
                this.readyCache.delete(datasetName); // Only cache successful ready response
              }
            }),
            this.catchReadyError(datasetName)
          )
      );
    }

    this.readyHelixCache.set(datasetName, readyPromise as Promise<FlamingoResponse.ReadyResponse>);
    this.readyCache.set(datasetName, readyPromise as Promise<FlamingoResponse.ReadyResponse>);
    return readyPromise as Promise<FlamingoResponse.ReadyResponse>;
  }

  handleUrlResponse(url: string) {
    return this.http.get<FlamingoTypes.ModeSelectResult>(url).pipe(
      map<FlamingoTypes.ModeSelectResult, FlamingoResponse.SelectResponse>((result) => {
        convertMicrosecondsToMilliseconds(result);
        return {
          result,
          type: FlamingoResponse.ResponseType.SelectSuccess,
        };
      }),
      catchError(() => {
        return of({
          status: 520,
          message: 'Server unreachable',
          type: FlamingoResponse.ResponseType.ServerError,
        } as FlamingoResponse.SelectResponse);
      })
    );
  }

  csvSelect(
    host: string,
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<string | Blob> {
    const url = `//${host}/helix/switch_plan_executions/v1`;

    return lastValueFrom(
      this.http
        .post<FlamingoResponse.HelixSelectResponse>(url, JSON.stringify(plan), {
          headers: this.getHeaders(signature, datasetName, vizToken, dbtMetricToken),
        })
        .pipe(
          // drop after 60 seconds to guard against long-running connections,
          // but not so low that legitimate long-running queries are canceled
          timeout(60000),
          concatMap((result) => {
            if (result.status === HelixResponseStatus.Succeeded && result.resultsType === 'URL') {
              return of(result.results);
            } else if (result.status === HelixResponseStatus.Executing) {
              const pollUrl = `//${host}/helix/switch_plan_executions/v1/${result.id}`;
              return this.http
                .get<FlamingoResponse.HelixCompleteResponse>(pollUrl, {
                  headers: this.getHeaders(signature, datasetName, undefined, dbtMetricToken),
                })
                .pipe(
                  map((pollResult) => {
                    if (pollResult.status === HelixResponseStatus.Succeeded && pollResult.resultsType === 'URL') {
                      return pollResult.results;
                    } else {
                      throw new Error('Unknown Error');
                    }
                  })
                );
            }

            // We should never get here since interceptor is handling "executing" response
            return throwError(() => new Error('Unknown Error'));
          })
        )
    );
  }

  // TODO: Delete the extra overrides once all services have been migrated to Helix
  select(
    host: string,
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.SelectResponse>;
  select(
    host: string,
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    useHelix: false,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.SelectResponse>;
  select(
    host: string,
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    useHelix: true,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.HelixSelectFinalResponse>;
  select(
    host: string,
    signature: string,
    datasetName: string,
    plan: FlamingoTypes.ModeSelectPlan,
    useHelix?: boolean | string,
    vizToken?: string,
    dbtMetricToken?: string
  ): Promise<FlamingoResponse.SelectResponse | FlamingoResponse.HelixSelectFinalResponse> {
    let url: string;

    // If useHelix overload is not used, then need to shift all remaining optional parameters
    if (typeof useHelix === 'string') {
      dbtMetricToken = vizToken;
      vizToken = useHelix;
    }

    if (useHelix !== false) {
      const startMark = this.observabilityService.mark(`${ObservabilityEvents.FLAMINGO_SELECT_REQUEST}`);
      url = `//${host}/helix/switch_plan_executions/v1`;
      return lastValueFrom(
        this.http
          .post<FlamingoResponse.HelixSelectResponse>(url, JSON.stringify(plan), {
            headers: this.getHeaders(signature, datasetName, vizToken, dbtMetricToken),
          })
          .pipe(
            concatMap((result) => {
              const handleCompleteResponse = (
                result: FlamingoResponse.HelixCompleteResponse
              ): Observable<FlamingoResponse.HelixSelectFinalResponse> => {
                this.observabilityService.measure(`${ObservabilityEvents.FLAMINGO_SELECT}`, {
                  start: startMark?.startTime,
                  detail: {
                    options: {
                      planId: plan.id,
                      datasetName,
                      token: vizToken || dbtMetricToken,
                      helix: true,
                      cacheHit: result?.cacheHit ?? false,
                    },
                  },
                });

                if (result.status === FlamingoResponse.HelixResponseStatus.Failed) {
                  const flamingoFailedResponse: FlamingoResponse.SelectErrorResponse = {
                    status: 400,
                    message: 'Bad Request',
                    type: FlamingoResponse.ResponseType.ServiceError,
                    payload: result.statusDetails,
                  };

                  return of(flamingoFailedResponse);
                } else if (result.resultsType === 'URL') {
                  return this.handleUrlResponse(result.results);
                } else if (result.resultsType === 'EMBEDDED') {
                  return of({
                    type: FlamingoResponse.ResponseType.SelectSuccess,
                    result: result.results,
                  });
                }
                // We should never get here since interceptor is handling "executing" response
                return of({
                  type: FlamingoResponse.ResponseType.UnknownError,
                  message: 'An unknown error occurred',
                  status: 520,
                });
              };

              // If result IS executing, make additional GET request to url with result ID
              // Interceptor will handle retry logic until status is no longer executing
              // and is a status of either "embedded" or "url"
              if (result.status === FlamingoResponse.HelixResponseStatus.Executing) {
                const url = `//${host}/helix/switch_plan_executions/v1/${result.id}`;
                return this.http
                  .get<FlamingoResponse.HelixCompleteResponse>(url, {
                    headers: this.getHeaders(signature, datasetName, undefined, dbtMetricToken),
                  })
                  .pipe(
                    switchMap((result) => {
                      return handleCompleteResponse(result);
                    })
                  );
              }

              return of(result).pipe(
                switchMap((result) => {
                  return handleCompleteResponse(result);
                })
              );
            }),
            this.catchSelectError(host, signature, datasetName)
          )
      );
    } else {
      url = `//${host}/selects`;

      return lastValueFrom(
        this.http
          .post<FlamingoTypes.ModeSelectResult>(url, JSON.stringify(plan), {
            headers: this.getHeaders(signature, datasetName, vizToken, dbtMetricToken),
          })
          .pipe(
            // drop after 60 seconds to guard against long-running connections,
            // but not so low that legitimate long-running queries are canceled
            timeout(60000),
            map<FlamingoTypes.ModeSelectResult, FlamingoResponse.SelectResponse>((result) => ({
              result,
              type: FlamingoResponse.ResponseType.SelectSuccess,
            })),
            this.catchSelectError(host, signature, datasetName)
          )
      );
    }
  }
}
